import { adminRoutes } from "./roles/admin/adminRoutes"; 
import { Navigate, Route, Routes } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import {
  checkToken as checkUserToken,
  profileInfo,
  logout as userLogout,
} from "./features/userAuthSlice";

import {
  checkToken as checkAdminToken,
  logout as adminLogout,
} from "./features/adminAuthSlice";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./styles/toastify-overrides.css";

import ScrollToTop from "./components/ScrollToTop";
import Preloader from "./components/Preloader";
import Maintenance from "./components/Maintenance";
import API, { setUnauthorizedHandler } from "./util/API";
import { fetchCurrencyRates } from "./features/currencySlice";
import Landing from "./pages/Landing";
import LegacyPathRedirect from "./components/LegacyPathRedirect";

function App() {
  const dispatch = useDispatch();
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [loading, setLoading] = useState(true);

  /* =========================
     API Health Check (NO AUTH)
  ========================== */
  const checkApiStatus = async () => {
    try {
      const res = await API.get("/", {
        headers: { Authorization: "" }, // 🔥 force no token
      });

      if (res.data?.success !== true) {
        throw new Error("API down");
      }

      setIsMaintenance(false);
    } catch (error) {
      console.error("API Health Check Failed:", error);
      setIsMaintenance(true);
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     Unauthorized Handler
  ========================== */
  useEffect(() => {
    setUnauthorizedHandler((type) => {
      // API.js clears both tokens from localStorage on 401/419.
      // Keep redux state consistent by clearing both auth slices too.
      dispatch(adminLogout());
      dispatch(userLogout());
    });
  }, [dispatch]);

  /* =========================
     Auth Restore on Reload
  ========================== */
  useEffect(() => {
    checkApiStatus();

    dispatch(checkUserToken());
    dispatch(checkAdminToken());

    // Load FX rates for currency conversion (cart/checkout display)
    dispatch(fetchCurrencyRates());

    const userToken = localStorage.getItem("userToken");
    if (userToken) {
      dispatch(profileInfo());
    }
  }, [dispatch]);

  /* =========================
     Route Renderer
  ========================== */
  const renderRoutes = (routes) =>
    routes.map((route, index) => {
      if (route.children) {
        return (
          <Route key={index} path={route.path} element={route.element}>
            {renderRoutes(route.children)}
          </Route>
        );
      }
      return <Route key={index} path={route.path} element={route.element} />;
    });

  /* =========================
     Loading
  ========================== */
  if (loading) {
    return (
      <Preloader />
    );
  }

  /* =========================
     Maintenance
  ========================== */
  if (isMaintenance) {
    return <Maintenance />;
  }

  return (
    <>
      <ScrollToTop />

      <ToastContainer
        position="top-right"
        autoClose={3600}
        theme="light"
        closeOnClick={false}
        pauseOnHover={false}
        pauseOnFocusLoss={false}
        draggable={false}
        newestOnTop
        limit={4}
        stacked
      />

      <div className="min-w-0 overflow-x-hidden">
        <Routes> 
          <Route path="/" element={<Landing />} />
          <Route
            path="/enteradmin/*"
            element={<LegacyPathRedirect fromBase="/enteradmin" toBase="/administrator" />}
          />
          {renderRoutes(adminRoutes)} 
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </>
  );
}

export default App;
